// @ts-nocheck
import { VerticalEntity } from 'store/verticals/types'
import { Period } from 'utils/types'
import { getYearFromPeriod, numberIsBetween } from 'containers/VerticalPage/common/utils'
import { byYearValueIsExist } from 'containers/VerticalPage/common/utils'
import { checkForNullValue } from 'containers/VerticalPage/nft/Overview/utils'

export const getLabelsForFinancialOverview = () => [
  { key: 'total', value: 'Total revenue' },
  { key: 'withoutVat', value: 'Total w/o VAT' },
  { key: 'reported', value: 'Reported' },
  { key: 'unreported', value: 'Unreported' },
]

export const getVerticalRevenueByTotalFromEntity = async (
  entityData: VerticalEntity | null, 
  period: Period,
  convertCurrency?: (amount: number, fromCurrency: string, toCurrency: string, year?: number) => Promise<number | null>,
  currentCurrency?: string
) => {
  if (!entityData) return []

  const { startYear, endYear } = getYearFromPeriod(period)

  let exactSum = 0
  let documentedSum = 0

  if (convertCurrency && currentCurrency) {
    try {
      for (let year = startYear; year <= endYear; year++) {
        let yearExactSum = 0
        let yearDocumentedSum = 0

        entityData.revenueSources.forEach(source => {
          const exactData = source.revenueByYear?.exact
          const documentedData = source.revenueByYear?.documented

          if (exactData?.[year]) {
            yearExactSum += exactData[year]
          }
          if (documentedData?.[year]) {
            yearDocumentedSum += documentedData[year]
          }
        })

        if (yearExactSum > 0) {
          const convertedExactSum = await convertCurrency(yearExactSum, 'USD', currentCurrency, year)
          if (convertedExactSum !== null) {
            exactSum += convertedExactSum
          }
        }
        if (yearDocumentedSum > 0) {
          const convertedDocumentedSum = await convertCurrency(yearDocumentedSum, 'USD', currentCurrency, year)
          if (convertedDocumentedSum !== null) {
            documentedSum += convertedDocumentedSum
          }
        }
      }
    } catch (error) {
      console.error('Error converting currency for pie chart data:', error)
    }
  } else {
    entityData.revenueSources.forEach(source => {
      const exactData = source.revenueByYear?.exact
      const documentedData = source.revenueByYear?.documented

      for (const key in exactData) {
        if (
          Object.prototype.hasOwnProperty.call(exactData, key) &&
          numberIsBetween(parseFloat(key), startYear, endYear)
        ) {
          exactSum += exactData[key]
        }
      }

      for (const key in documentedData) {
        if (
          Object.prototype.hasOwnProperty.call(documentedData, key) &&
          numberIsBetween(parseFloat(key), startYear, endYear)
        ) {
          documentedSum += documentedData[key]
        }
      }
    })
  }

  if (exactSum === 0) return [{ name: 'Documented', value: documentedSum }]

  return [
    { name: 'Estimated', value: exactSum },
    { name: 'Documented', value: documentedSum },
  ]
}

export const getTotalRevenueFromEntity = async (
  entityData: VerticalEntity | null,
  period: Period,
  noDataAsNA: boolean = false,
  convertCurrency?: (amount: number, fromCurrency: string, toCurrency: string, year?: number) => Promise<number | null>,
  currentCurrency?: string,
) => {
  if (!entityData) return []

  const { startYear, endYear } = getYearFromPeriod(period)
  const data = entityData.totals.byYear

  // const years = new Set<string>()
  // Currently, these are the years we need to display, even when there is no data for them.
  const years = new Set(['2019', '2020', '2021', '2022', '2023'])

  const withoutVatIsExist = byYearValueIsExist(data, 'totalWithoutVat')

  for (const category in data) {
    Object.keys(data[category]).forEach(year => years.add(year))
  }

  const result = await Promise.all(
    Array.from(years)
      .sort()
      .map(async year => {
        const yearNumber = parseInt(year)
        const yearDataObject: { [key: string]: string | number | null } = {
          date: year,
          exact: checkForNullValue(data?.exact?.[year], noDataAsNA),
          total: checkForNullValue(data?.total?.[year], noDataAsNA),
          documented: checkForNullValue(data?.documented?.[year], noDataAsNA),
          estimated: checkForNullValue(data?.exact?.[year], noDataAsNA),
          reported: checkForNullValue(data?.reportedByYear?.[year], noDataAsNA),
          unreported: checkForNullValue(data?.unReportedByYear?.[year], noDataAsNA) || 0,
        }

        if (withoutVatIsExist) {
          yearDataObject.withoutVat = data?.totalWithoutVat?.[year] || 0
        }

        if (convertCurrency && currentCurrency) {
          const numericKeys = ['exact', 'total', 'documented', 'estimated', 'reported', 'unreported', 'withoutVat']

          for (const key of numericKeys) {
            if (typeof yearDataObject[key] === 'number' && yearDataObject[key] !== null) {
              try {
                const convertedValue = await convertCurrency(
                  yearDataObject[key] as number,
                  'USD',
                  currentCurrency,
                  yearNumber,
                )
                if (convertedValue !== null) {
                  yearDataObject[key] = convertedValue
                }
              } catch (error) {
                console.error(`Error converting ${key} for year ${year}:`, error)
              }
            }
          }
        }

        return yearDataObject
      }),
  )

  return result.filter(item => numberIsBetween(+(item.date as string), startYear, endYear))
}
